export const ORDERING_PROVIDER_PROMPT = `
   Ordering provider - if not listed on exam page, check cover page to see who recipient of document is - cross check with list of providers,
   if the name matches, this is likely the ordering provider. Also sometimes says 'exam requested by'
`;

export const PRESCRIBING_PROVIDER_PROMPT = `
  Prescribing provider - check cover page for the prescribing provider if needed, cross check with list of providers,
  if the name matches, this is likely the prescribing provider. Also sometimes says 'prescribed by' or 'prescriber' 
`;

export const SUPPLY_DURATION_PROMPT = `
  Extract the medication quantity, supply duration, or both — as a single free-text string.

  Format:
  - Both stated: "<quantity> per <N> days" (e.g., "30 capsules per 30 days", "75 per 25 days",
    "1 mL(s) every 28 days"). Prefer this form whenever both pieces appear anywhere on the
    document — including in dosing instructions like "inject 1 mL every 28 days" — even if a
    separate "Days Supply" field is also present.
  - Quantity only (e.g., topicals, ointments, single-tube Rx): just the quantity with unit if given
    (e.g., "118.28", "1 tube", "60 mL").
  - Duration only: just the duration (e.g., "30 days").
  - Null only when neither is stated.

  Strip leading zeros from numbers ("030 days" → "30 days").
`;

export const MEDICATION_NAME_PROMPT = `
  Extract the medication name only from this text. Ignore all other information like dosage, frequency, or route. Return just the name in proper case. 
  For example, if the doc says ZEPBOUND 2.5 MG/0.5 ML PEN, just pull Zepbound, and ignore the 2.5 MG/0.5 ML PEN

  Text: "1 tab of metoprolol tartrate 25mg twice a day"
  Medication name: Metoprolol Tartrate

  Text: "Take lisinopril every morning"
  Medication name: Lisinopril

  Text: "ZEPBOUND 2.5 MG/0.5 ML PEN"
  Medication name: Zepbound
`;

export const LAB_NAME_PROMPT = `
Extract only the laboratory test name from the given text. Ignore all other information such as date, time, or location. Return only the lab name.

## Output Format
Always return the lab name in proper case.

## Rules
- Default to the standard abbreviation if one exists
- If no abbreviation rule applies, return the lab name as written (in proper case)
- Do not extract labs listed under "pending labs" (they are not completed)
- Only extract labs with results

## Abbreviation Mappings

### Basic Cases
- "Comprehensive metabolic panel" or "Comp metabolic panel" → CMP
- "Basic metabolic panel" or "Basic metabolic profile" → BMP
- "Microalbumin/creatinine mass ratio," "urine albumin-to-creatinine ratio," or "UACR" → Microalbumin/Creatinine Ratio
- "CBC w/ diff," "CBC w/ diff auto," "CBC w/o diff," "CBC differential auto," or similar → CBC
- "Hemoglobin A1C" or similar → HgA1c
- "Thyroid stimulating hormone" or similar → TSH
- "Free T3" and "Free T4" (both listed together) → T3 T4
- "Lipid panel with calculated LDL" or similar → Lipids
- "Prostate-specific antigen" → PSA
- "Free triiodothyronine" → T3
- "Thyroxine" → T4

## Examples

Text: "Lab Results: CBC" → Lab name: CBC

Text: "COLOGUARD" → Lab name: Cologuard

Text: "FIT" → Lab name: FIT

Text: "LIPID PANEL" → Lab name: Lipid Panel

Text: "Hepatitis" → Lab name: Hepatitis
`;

export const COLLECTION_DATE_PROMPT = `
    These should be the date collected, NOT the date ordered or reported/resulted. Look for:

    - "Date Collected"
    - "Collection Date"
    - "Coll. Date"
    - "Coll"

    Should NOT be:
    - "Order Date"
    - "Ordered"
    - "Report:"
    - "Result Recd"
    - "Recd"
    - "Verf"
    - "Entered"
    - "Run Date"

    Format as YYYY-MM-DD.
  `;

export const COMPANY_NAME_OTHER_PROMPT =
  "Company name written in free text when the company is not one of the companyName options.";

export const PHARMACY_COMPANY_NAME_PROMPT =
  "The name of the pharmacy or organization that issued or sent this document.";

export const IMAGING_COMPANY_NAME_PROMPT =
  "The name of the imaging center or facility that produced or sent this document.";

export const PRIOR_AUTHORIZATION_COMPANY_NAME_PROMPT =
  "The name of the insurance company or organization that issued or sent this document.";

export const DOCUMENT_TYPE_PROMPT =
  "The type or category of the document as stated on the document itself (e.g. its title or header).";

export const CLINIC_CALL_REASON_PROMPT = "The reason for the clinic call or inquiry captured on this form.";

export const HOME_HEALTH_COMPANY_NAME_PROMPT = `
Extract the home health company name only from the given text.

## Output Format
Return the final company name all in lowercase.

## Rules
- Ignore all other information such as date, time, or location
- Return just the name in proper case
- If the text contains "Home Health" or "HH" at the end, strip those words
- If "Home Health" is not at the end of the string, do not remove it

## Examples

### Basic Cases
Text: "ABC Home Health"
→ Home Health Company Name: abc

Text: "ABC HH"
→ Home Health Company Name: abc

Text: "ABC Home Health Services"
→ Home Health Company Name: abc home health services

### Special Cases (Exceptions)
Text: "AHC Home Health of Reno, LLC"
→ Home Health Company Name: ahc

Text: "AHC Home Health of Reno, LLC dba Advanced Home Health and Hospice of Northern Nevada"
→ Home Health Company Name: ahc

Text: "CenterWell Home Health"
→ Home Health Company Name: centerwell

Text: "Saint Mary's Home Health …"
→ Home Health Company Name: saint mary's

Text: "Eden Home Health"
→ Home Health Company Name: eden

Text: "Renown Home Health" or "Renown Home Care"
→ Home Health Company Name: renown

`;

export const DIAGNOSIS_CODE_PROMPT = `
    Extract the diagnosis code from the text.

    ## Pattern Requirements
    The diagnosis code must follow this regex pattern:
    /\b[A-Z][0-9A-Z]{1,6}(?:\.[0-9A-Z]{1,4})?\b/g
    ## Important Notes
    - **Must start with a letter** (A-Z)
    - **Do not confuse the letter 'I' with the number '1'**
    - Look for codes in parentheses, after colons, or standalone

    ## Code Format Examples

    ### ICD-10 Codes (Most Common)
    - Input: "J45.909 (Asthma)."
      Output: J45.909

    - Input: "E11.9 (Diabetes)."
      Output: E11.9

    - Input: "A00 (Cholera)"
      Output: A00

    - Input: "M16.5"
      Output: M16.5


    ## Extraction Rules
    1. Look for codes in parentheses first
    2. Check for codes after medical terminology
    3. Extract only the code portion, not descriptive text
    4. Maintain exact formatting (including decimals)
    `;

export const HOSPITAL_ABBREV_PROMPT = `
Extract the hospital abbreviation from the text.

## Abbreviation Rules
- Take the **first letter of each word** in the hospital name
- Ignore common words like "the", "at", "of", "and", "for", "a"
- Maintain the exact order of words as they appear
- When forming the abbreviation, use "MC" for "Medical Center", "RMC" for "Regional Medical Center", and "H" for "Hospital". This applies even if an abbreviation is already listed in the text — always derive from the full name.

## Examples

### Input: "Patient was admitted to Saint Mary's Regional Medical Center"
**Output**: SMRMC

### Input: "Discharged from Northern Nevada Medical Center"
**Output**: NNMC

### Input: "NNM - Northern Nevada Medical Center"
**Output**: NNMC
(Ignore the listed "NNM" — derive from full name: NN + MC)

### Input: "Transferred to NNS - Northern Nevada Sierra Medical Center"
**Output**: NNSMC

### Input: "Admitted to Renown Regional Medical Center (RR)"
**Output**: RRMC

### Input: "Sent to Valley Presbyterian Hospital"
**Output**: VPH

### Input: "Seen at South Lyon Medical Center"
**Output**: SLMC

## Notes
- Always return the abbreviation in uppercase letters
- Do not include periods or spaces between letters
`;

export const SCHEDULING_PRIORITY_PROMPT = `
    Urgency or priority of the document. Look for a priority field or note with one of these key words: 'Routine', 'Urgent', 'STAT'.
    - "Routine" is the default priority. If unclear or no priority is found, default to "Routine".
    - Make sure that the priority is explicitly marked in the document, usually with a checkbox next to the "Routine", "Urgent", or "STAT" text.
    `;

export const PROGRESS_NOTE_ENCOUNTER_TYPE_PROMPT = `
The stage of physical/occupational/speech therapy treatment this note documents. One of:
- "Intake": a registration or demographics form completed before treatment begins.
- "Initial Evaluation": the clinician's first evaluative visit, establishing a diagnosis and plan of care.
- "Rehab": an ongoing treatment or progress visit occurring after the initial evaluation.

Return null if the encounter stage is not indicated.
`;

export const IS_NEWBORN_PROMPT = `
True when the document indicates the patient is a newborn — e.g., 'newborn', 'NB', nursery/neonatal context, or similar cues. False otherwise.
`;

export const BODY_PART_PROMPT = `
Extract and return the body part that was examined from the given text.

## Output Format
Return the body part in proper case.

## Rules
- Always include any pertinent descriptors that clarify the exam location or type
- If the body part has a side specified (e.g., left, right, bilateral), include it in the output
- If the exam specifies a spinal region, return the region with the word spine (e.g., "cervical spine," "lumbar spine") instead of just "spine"
- If the exam type is described as "coronary calcium score," "coronary cardiac scoring," "coronary calcium scan," or any similar variation, set the body part field to Cardiac Scoring

## Examples

Text: "MRI of left leg" → Body Part: Left Leg

Text: "CT scan of right arm" → Body Part: Right Arm

Text: "X-ray of cervical spine" → Body Part: Cervical Spine

Text: "MRI lumbar spine" → Body Part: Lumbar Spine

Text: "CT coronary calcium score" → Body Part: Cardiac Scoring

`;

export const BODY_PARTS_PROMPT = `
Extract every anatomical area examined in the document.

## Output Format
Return an array of body parts in proper case. Return null when no body part can be determined.

## Rules
- Return each distinct anatomical area once.
- Keep spinal regions specific (for example, "Cervical Spine" or "Lumbar Spine").
- Do not include laterality such as left, right, bilateral, or unilateral.
- Do not include the imaging modality, procedure, scan type, contrast, or other study descriptors.

## Examples

Text: "CT chest, abdomen, and pelvis with contrast" → Body Parts: ["Chest", "Abdomen", "Pelvis"]

Text: "MRI of the left knee" → Body Parts: ["Knee"]
`;

export const SIGNATURE_REQUIRED_PROMPT = `
Whether this document requires a physician or provider signature before it can be processed or returned. True when the document explicitly requests a signature that has not yet been provided — look for: a blank "Physician Signature" / "Doctor's Signature" / "Provider Signature" line; language such as "Please sign and return", "Signature required", "Physician signature required", "Awaiting signature"; or a signature block clearly left unsigned. False when the document already bears the physician's signature, or when no signature is being requested (e.g. a confirmation or completed form). Null only when it cannot be determined.
`;

export const ABNORMAL_RESULT_PROMPT = `
Whether a physician or clinician indicates this lab or imaging result is abnormal anywhere in the document — in a narrative impression, comment, or conclusion, not only a structured 'abnormal' flag/checkbox on the report itself. True whenever such a statement appears (e.g., 'abnormal finding,' 'markedly elevated,' an explicit abnormal flag), even if most values on the page are normal. False when the document indicates normal/unremarkable results or makes no such statement.
`;

export const FOLLOW_UP_REQUIRED_PROMPT = `
Whether a physician or clinician indicates this lab or imaging result requires follow-up anywhere in the document — in a narrative impression, comment, or conclusion, not only a structured 'follow_up' flag/checkbox on the report itself. True whenever such a statement appears (e.g., 'follow-up required,' 'suggest follow-up,' 'clinical correlation recommended'), even if most values on the page are normal. False when the document indicates no follow-up is needed or makes no such statement.
`;

export const IMPRESSION_PROMPT = `
Follow these steps, in order, and stop at the first one that matches:

1. Look for a section labeled "Impression" (case-insensitive). The label may be on its own line, on the same line as the content, or visually adjacent to it. If found, capture the full section — everything after the label up to (but not including) the next section label or the end of the document.
2. Otherwise, do the same for the first section labeled "Conclusion", "Interpretation", "Diagnostic Interpretation", "Assessment", or "Summary", in that order.
3. Otherwise, do the same for a section labeled "Findings".
4. Otherwise, look for a section or heading labeled "Diagnosis" (case-insensitive):
   - If it is followed by inline prose on the same line (e.g., "Diagnosis: <content>"), capture the full section — everything after the label up to (but not including) the next section label or the end of the document.
   - If it is a heading on its own line, or repeats as a label prefixed to each of several consecutive lines (e.g., a table-row layout like "DIAGNOSIS Normal sinus rhythm" / "DIAGNOSIS Normal ECG") — this structure appears on short structured-form reports such as ECG/EKG interpretations and sports-clearance/physical forms, rather than narrative reports — scan the lines that follow (or share) the label up to a structural boundary: a new section heading, a "Confirmed by <name> on <date>" attestation/signature line, or the end of the document. Strip the label from each line, including a "Result:" sub-label if present (e.g., "Result: Normal sinus rhythm"). From the remaining lines, keep only those stating an actual diagnostic/interpretive finding (e.g., "Normal sinus rhythm", "Sinus tachycardia", "Normal ECG"), and skip administrative, instructional, or disclaimer lines that are not themselves findings (e.g., "Clinical correlation required", "Correlate clinically", "No previous ECGs available for comparison", "No prior studies available"). Join the kept findings, in the order they appear, with "; " (semicolon followed by a space).
5. If a section was captured under steps 1-3, or under the inline-prose case of step 4, return its wording verbatim — do not summarize, paraphrase, condense, or omit any of it, and do not include the matched heading itself. If that captured text spans multiple lines, join it into a single line by replacing each line break with a semicolon and a space ("; "); never smush the lines together with no separator, and do not emit empty segments for blank lines between them. If findings were collected under the heading-only case of step 4, return the semicolon-joined string produced there.
6. Treat differences in punctuation, whitespace, capitalization, or OCR layout as equivalent labels. Return null only when none of these labeled sections is present.

Examples your parser should produce:
- Input: "IMPRESSION: Prominent bowel loops in the epigastric region with to and fro movement of intraluminal content. Findings are nonspecific; clinical correlation is recommended.\\n\\nRECOMMENDATION: Follow up as needed."
  Output: Prominent bowel loops in the epigastric region with to and fro movement of intraluminal content. Findings are nonspecific; clinical correlation is recommended.

- Input: "Impression:\\nNo acute findings."
  Output: No acute findings.

- Input: "FINDINGS: The heart is normal in size.\\n\\nCONCLUSION: No acute cardiopulmonary abnormality."
  Output: No acute cardiopulmonary abnormality.

- Input: "FINDINGS: The heart is normal in size. Lungs are clear bilaterally.\\n\\nRECOMMENDATION: Routine follow-up."
  Output: The heart is normal in size. Lungs are clear bilaterally.

- Input: "IMPRESSION:\\n1. Mild cardiomegaly.\\n2. No acute infiltrate.\\n3. Clinical correlation recommended."
  Output: 1. Mild cardiomegaly.; 2. No acute infiltrate.; 3. Clinical correlation recommended.

- Input: "Diagnosis\\nResult: Normal sinus rhythm\\nNormal ECG\\nConfirmed by Smith MD, Jane (7) on 1/2/2026 2:48:25 PM"
  Output: Normal sinus rhythm; Normal ECG

- Input: "Diagnosis: Acute otitis media, right ear.\\n\\nPLAN: Amoxicillin 400mg/5mL, 5mL twice daily for 10 days."
  Output: Acute otitis media, right ear.

- Input: "DIAGNOSIS Clinical correlation required\\nDIAGNOSIS Normal sinus rhythm\\nDIAGNOSIS Normal ECG\\nDIAGNOSIS No previous ECGs available\\n\\nAttachments Linked to Order 696339225"
  Output: Normal sinus rhythm; Normal ECG
`;

export const STUDY_TYPE_PROMPT = `
The specific named study, exam, or protocol reported in the document, beyond the broad imaging category. Use the report title or labels such as "Study", "Exam", "Procedure", "Test", or "Protocol". Return a concise label that preserves clinically meaningful details such as contrast, complete/limited, stress/rest, or the acquisition method when stated.

Examples include "OCT Macula", "Transthoracic Echocardiogram", "Lexiscan Myocardial Perfusion", and "CT Abdomen/Pelvis With Contrast". Do not use findings, the diagnostic impression, or an anatomical body part by itself. Return null when the document does not identify a study more specific than the broad imaging category.
`;

export const BODY_PART_LATERALITY_PROMPT = `
Extract the side of the body (laterality) referenced in the document, if any.

## Rules
- Return "Left" if the document specifies the left side (e.g., "left knee," "L knee").
- Return "Right" if the document specifies the right side (e.g., "right knee," "R knee").
- Return "Other" if a side is mentioned but is not clearly left or right (e.g., "bilateral," "midline").
- Return null if no side is specified.

## Examples

Text: "MRI of left leg" → Laterality: Left

Text: "CT scan of right arm" → Laterality: Right

Text: "X-ray of bilateral knees" → Laterality: Other

Text: "MRI lumbar spine" → Laterality: null

`;

export const GIVEN_NAME_PROMPT = `
    The person's first (given) name only.

    A full name decomposes as: [prefix] given [middle...] family [suffix].
    Strip any title/prefix (e.g., "Mr.", "Dr.") and any suffix (e.g., "Jr.", "MD")
    before assigning the given name.
    Keep hyphenated given names intact (e.g., "Mary-Jane").
    Names may be written as "First Middle Last" or "Last, First Middle".

    Examples:
    Input: "John Michael Smith"             -> "John"
    Input: "Smith, John Michael"            -> "John"
    Input: "Dr. Mary-Jane Watson, MD"       -> "Mary-Jane"
    Input: "Garcia Lopez, Maria Del Carmen" -> "Maria"
  `;

export const FAMILY_NAME_PROMPT = `
    The full last (family) name. Capture the ENTIRE surname as a single string,
    including:
    - particles (e.g., "van der Berg")
    - hyphenated / double-barreled surnames (e.g., "Smith-Jones")
    - Hispanic two-word surnames (e.g., "Garcia Lopez")

    In "Last, First Middle" format, everything BEFORE the comma is the family name.
    Do NOT move surname particles or a second surname word into the middle name.
    Strip trailing suffixes (Jr., III, MD) — those belong in the suffix field.

    Examples:
    Input: "John Michael Smith"            -> "Smith"
    Input: "Ludwig van der Berg"           -> "van der Berg"
    Input: "Maria Del Carmen Garcia Lopez" -> "Garcia Lopez"
    Input: "De La Cruz, Ana"               -> "De La Cruz"
    Input: "Smith-Jones, Robert A."        -> "Smith-Jones"
    Input: "Robert Brown Jr."              -> "Brown"
  `;

export const MIDDLE_NAME_PROMPT = `
    Extract the middle name from this medical referral document. The middle name may appear alongside the first and last name under headings such as:
    - "Patient Information"
    - "Patient Name"
    - Towards the top of the page

    The full name might be written in formats such as:
    1. First Middle Last (e.g., John Michael Smith)
    2. Last, First Middle (e.g., Smith, John Michael)
    3. Last, First M. (e.g., Smith, John M.)

    If only an initial is present (e.g., John M. Smith), return the initial only.
    If more than one middle name is present, join them with a space (e.g., "Anna Maria").
    Do NOT place surname particles (van, de la) or a second surname word here, those stay with the family name.

    Examples:
    Input: "Patient Name: John Michael Smith"
    Output: "Michael"

    Input: "Name: Smith, John M."
    Output: "M"

    Input: "Patient: Sarah Elizabeth Johnson"
    Output: "Elizabeth"

    Input: "Name: Robert Brown"
    Output: null

    Input: "Patient Information: Williams, James A."
    Output: "A"

    Input: "Maria Del Carmen Garcia Lopez"
    Output: "Del Carmen"

    Input: "Ludwig van der Berg"
    Output: null
  `;

export const NAME_PREFIX_PROMPT = `
    Honorific titles appearing BEFORE the name, returned as an array.
    Examples: Mr., Mrs., Ms., Dr., Prof.
    Return null if no prefix is present.

    Examples:
    Input: "Dr. Mary-Jane Watson" -> ["Dr."]
    Input: "John Smith"           -> null
  `;

export const NAME_SUFFIX_PROMPT = `
    Name suffixes appearing AFTER the name, returned as an array. Includes BOTH:
    - generational suffixes: Jr., Sr., II, III, IV
    - provider credentials: MD, DO, NP, PA-C, RN, PhD

    Return null if no suffix is present.

    Examples:
    Input: "Robert Brown Jr."     -> ["Jr."]
    Input: "Mary-Jane Watson, MD" -> ["MD"]
    Input: "James Lee III, MD"    -> ["III", "MD"]
    Input: "John Smith"           -> null
  `;

export const IS_NOT_THEIR_PATIENT_PROMPT =
  "True when the receiving (external) clinic has marked that this is NOT their patient and sent the plan " +
  "of care back — i.e. the patient is not seen / not established at that practice. These are plans of " +
  "care originally sent out to an external clinic and returned to sender. The indicator is often a typed " +
  `or handwritten note, frequently on the cover page of the incoming fax, with language like "not our ` +
  `patient", "not our pt", "patient not seen here", "not a patient at this office/practice", "no record ` +
  `of this patient", "wrong office", "return to sender", "please remove", or the patient's name crossed ` +
  "out. False or null otherwise.";
